import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Snackbar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import TelegramIcon from "@mui/icons-material/Telegram";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useAuth } from "../../contexts/AuthContext";
import {
  STATE_TO_AREAS,
  ALL_STATES,
} from "../deals/dealList/constants/malaysiaLocations";
import AreaBrowsePickerDialog from "../deals/dealList/filters/AreaBrowsePickerDialog";
import CustomButton from "../ui/CustomButton";
import api, { userAPI } from "../../services/api";
import logger from "../../utils/logger";
import { trackEvent } from "../../utils/analytics";
import {
  REMINDER_CATEGORIES,
  allReminderCategoryValues,
  hasReminderCategorySelection,
  isAllReminderCategoriesSelected,
  normalizeReminderCategoriesForForm,
  reminderAreasFromDealAreaPreference,
  reminderCategoriesFromDealCategoryPreference,
} from "./reminderPrefillFromDealFilters";
import {
  formatReminderDigestDateMalaysia,
  REMINDER_DAY_OPTIONS,
  REMINDER_NOTIFICATION_TIMES,
  reminderChipSx,
  reminderFieldHintSx,
  reminderFieldLabelSx,
  reminderHeroSubtitleSx,
  reminderHeroTitleSx,
  reminderInlineErrorSx,
  reminderOptionCardSubtitleSx,
  reminderOptionCardTitleSx,
  reminderPageTitleSx,
  reminderSectionMetaSx,
  reminderSectionSubtitleSx,
  reminderSectionTitleSx,
} from "./reminderScheduleConstants";
import UserMenu from "../common/UserMenu";
import { uiNeutrals } from "../../constants/uiNeutrals";
import { closeIconButtonSx } from "../../constants/closeIconButtonSx";
import { appBackgroundColor } from "../../theme";
import {
  DEALS_HEADER_BOTTOM_PADDING_PX,
  DEALS_HEADER_ROW_MIN_HEIGHT_PX,
  DEALS_HEADER_TOP_GAP_PX,
  dealsScrollContentPaddingTop,
} from "../../constants/dealsShellLayout";

const reminderFrostedChromeBg = alpha(appBackgroundColor, 0.86);

const REMINDER_DIGEST_EXAMPLE_DEAL_COUNT = 2;

/** Sample digest — mirrors cron `sendDealReminders` / `formatMessage` layout (illustrative). */
const REMINDER_DIGEST_EXAMPLE = {
  filterLines: ["📍 Mont Kiara, Bangsar", "🍽 Japanese", "⏰ Lunch"],
  sections: [
    {
      label: "EXPIRING TODAY",
      filterDeals: [
        {
          num: "1.",
          brand: "Ramen Shack",
          title: "Spend RM38: ramen + side + drink",
          schedule: "📅 Ends today",
          linkHint: "https://loopsfood.com/deal/ABC12",
        },
      ],
    },
    {
      label: "EXPIRING TOMORROW",
      filterDeals: [
        {
          num: "2.",
          brand: "Sushi Bar",
          title: "20% off lunch sets",
          schedule: "📅 Ends tomorrow",
          linkHint: "https://loopsfood.com/deal/XYZ99",
        },
      ],
    },
  ],
  footerHint: "⚙️ Update your filters",
};

const GLASSY_SHADOW = "0 4px 18px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.65)";

const glassyButtonWrapperSx = {
  bgcolor: uiNeutrals.frostedFill,
  backdropFilter: "blur(22px) saturate(1.08)",
  WebkitBackdropFilter: "blur(22px) saturate(1.08)",
  boxShadow: GLASSY_SHADOW,
  border: "1px solid rgba(255,255,255,0.55)",
  width: 40,
  height: 40,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  position: "relative",
};

const glassyIconButtonSx = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  borderRadius: "50%",
  p: 0,
  color: "#000",
  WebkitTapHighlightColor: "transparent",
  "&&, &&:hover, &&:active, &&:focus, &&:focus-visible, &&.Mui-focusVisible": {
    bgcolor: "transparent",
    boxShadow: "none",
    outline: "none",
  },
};

const REMINDER_ONBOARDING_DRAFT_KEY = "loops_reminder_onboarding_draft_v2";
const REMINDER_ONBOARDING_DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** Caps reminder UI width on large screens (~phone-readable column). */
const REMINDER_PAGE_MAX_CONTENT_WIDTH_PX = 480;

const DEFAULT_NOTIFY_SECTIONS = ["expiring_today"];

const NOTIFY_SECTION_OPTIONS = [
  {
    key: "expiring_today",
    label: "Deals based on your filters",
    description: "Deals from the locations and categories you selected.",
  },
  {
    key: "saved_expiring",
    label: "Your saved deals",
    description: "Deals you saved from the deals page.",
  },
];

const normalizeNotifySectionsForClient = (value) => {
  const allowed = new Set(NOTIFY_SECTION_OPTIONS.map((option) => option.key));
  const raw = Array.isArray(value) ? value : value ? [value] : [];
  const normalized = raw
    .map((v) => (typeof v === "string" ? v.trim().toLowerCase() : ""))
    .filter((v) => allowed.has(v));
  const unique = [...new Set(normalized)];
  return unique.length > 0 ? unique : [...DEFAULT_NOTIFY_SECTIONS];
};

const inferStateFromAreas = (areas) => {
  if (!areas?.length) return "";
  const first = areas[0];
  for (const [state, stateAreas] of Object.entries(STATE_TO_AREAS)) {
    if (stateAreas.includes(first)) return state;
  }
  return "";
};

const readReminderOnboardingDraft = () => {
  try {
    const raw = localStorage.getItem(REMINDER_ONBOARDING_DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== 2) return null;
    if (typeof parsed.updatedAt !== "number") return null;
    if (Date.now() - parsed.updatedAt > REMINDER_ONBOARDING_DRAFT_MAX_AGE_MS) {
      localStorage.removeItem(REMINDER_ONBOARDING_DRAFT_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const writeReminderOnboardingDraft = ({
  selectedAreas,
  selectedCategories,
  selectedNotifySections,
  daysOfWeek,
  notificationTime,
  selectedState,
  currentStep,
  pendingPostLoginSave,
}) => {
  try {
    const prev = readReminderOnboardingDraft();
    const keepPending =
      typeof pendingPostLoginSave === "boolean"
        ? pendingPostLoginSave
        : Boolean(prev?.pendingPostLoginSave);
    localStorage.setItem(
      REMINDER_ONBOARDING_DRAFT_KEY,
      JSON.stringify({
        version: 2,
        updatedAt: Date.now(),
        selectedAreas,
        selectedCategories,
        selectedNotifySections,
        daysOfWeek,
        notificationTime,
        selectedState,
        currentStep,
        pendingPostLoginSave: keepPending,
      }),
    );
  } catch {
    // ignore quota / private mode
  }
};

const clearReminderOnboardingDraft = () => {
  try {
    localStorage.removeItem(REMINDER_ONBOARDING_DRAFT_KEY);
  } catch {
    // ignore
  }
};

const TelegramReminderSetup = () => {
  // Ref for the scrollable content area — used to check scrollTop in pull-to-refresh guard
  const scrollContentRef = useRef(null);

  // Block pull-to-refresh on this page (iOS Safari) without blocking inner scroll
  useEffect(() => {
    let startY = 0;
    const onTouchStart = (e) => {
      startY = e.touches[0].clientY;
    };
    const onTouchMove = (e) => {
      const deltaY = e.touches[0].clientY - startY;
      // Only block the pull-down gesture when the inner scroll container
      // is also at the very top (scrollTop === 0). This allows scrolling
      // back up after the user has scrolled down into the content.
      const contentScrollTop = scrollContentRef.current?.scrollTop ?? 0;
      if (deltaY > 0 && contentScrollTop === 0) e.preventDefault();
    };
    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
    };
  }, []);

  const lightDividerSx = {
    borderColor: "rgba(0,0,0,0.06)",
  };

  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  /** Reminder fetch + prefill; keep true for guests until bootstrap effect runs */
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [reminder, setReminder] = useState(null);
  const [telegramConnected, setTelegramConnected] = useState(false);
  const prevTelegramConnectedRef = useRef(false);

  useEffect(() => {
    if (telegramConnected && !prevTelegramConnectedRef.current) {
      trackEvent("reminder_connected");
    }
    prevTelegramConnectedRef.current = telegramConnected;
  }, [telegramConnected]);

  // Form state
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedNotifySections, setSelectedNotifySections] = useState(
    DEFAULT_NOTIFY_SECTIONS,
  );
  const [daysOfWeek, setDaysOfWeek] = useState([]); // 1=Mon .. 7=Sun; [] = daily
  const [notificationTime, setNotificationTime] = useState("09:00");
  const [telegramLink, setTelegramLink] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [areaPickerOpen, setAreaPickerOpen] = useState(false);
  const [dialogStep, setDialogStep] = useState("state"); // "state" | "area"
  const [dialogBrowsingState, setDialogBrowsingState] = useState("");
  const [dialogQuery, setDialogQuery] = useState("");
  const [currentStep, setCurrentStep] = useState(1); // 1: welcome, 2: filters, 3: notification, 4: done
  const [isOnboarding, setIsOnboarding] = useState(true); // Track if user is going through onboarding
  const [step2RequiredError, setStep2RequiredError] = useState(false);
  /** Step 4: after auto-open / connected, show footer Back to reload /reminders */
  const [step4TeleGateDone, setStep4TeleGateDone] = useState(false);
  /** 0–1 fill progress for Connect Telegram auto-open (5s) */
  const [teleOpenProgress, setTeleOpenProgress] = useState(0);
  const step4TeleIntervalRef = useRef(null);
  const step4TeleOpenOnceRef = useRef(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const guestDraftHydratedRef = useRef(false);
  const pendingPostLoginAutoSaveRef = useRef(false);
  const postLoginAutoSaveAttemptedRef = useRef(false);
  const dealFiltersPrefilledRef = useRef(false);
  const [exampleDigestDialogOpen, setExampleDigestDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const navLinks = useMemo(
    () => [
      { to: "/home", label: "Home" },
      { to: "/deals", label: "Browse" },
      ...(isAuthenticated ? [] : [{ to: "/login", label: "Login" }]),
    ],
    [isAuthenticated],
  );

  // Clear legacy reminder draft from the old "save then login" flow (not v2 onboarding).
  useEffect(() => {
    if (!isAuthenticated) return;
    try {
      localStorage.removeItem("loops_reminder_draft_v1");
      sessionStorage.removeItem("loops_reminder_draft_v1");
    } catch (e) {
      // ignore
    }
  }, [isAuthenticated]);

  useLayoutEffect(() => {
    if (isAuthenticated) {
      guestDraftHydratedRef.current = false;
      return;
    }
    guestDraftHydratedRef.current = true;
    const parsed = readReminderOnboardingDraft();
    if (!parsed) return;
    const areas = parsed.selectedAreas ?? [];
    setSelectedAreas(areas);
    setSelectedCategories(
      normalizeReminderCategoriesForForm(parsed.selectedCategories ?? []),
    );
    setSelectedNotifySections(
      normalizeNotifySectionsForClient(parsed.selectedNotifySections),
    );
    setDaysOfWeek(
      Array.isArray(parsed.daysOfWeek) ? [...parsed.daysOfWeek] : [],
    );
    setNotificationTime(parsed.notificationTime ?? "09:00");
    const stateName =
      parsed.selectedState && ALL_STATES.includes(parsed.selectedState)
        ? parsed.selectedState
        : inferStateFromAreas(areas);
    setSelectedState(stateName);
    const step = parsed.currentStep;
    setCurrentStep(
      typeof step === "number" ? Math.min(Math.max(step, 1), 4) : 1,
    );
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsOnboarding(true);
      pendingPostLoginAutoSaveRef.current = false;
      postLoginAutoSaveAttemptedRef.current = false;
      setLoading(false);
      return;
    }

    fetchReminder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated || !guestDraftHydratedRef.current) return;
    const t = setTimeout(() => {
      writeReminderOnboardingDraft({
        selectedAreas,
        selectedCategories,
        selectedNotifySections,
        daysOfWeek,
        notificationTime,
        selectedState,
        currentStep,
      });
    }, 400);
    return () => clearTimeout(t);
  }, [
    isAuthenticated,
    selectedAreas,
    selectedCategories,
    selectedNotifySections,
    daysOfWeek,
    notificationTime,
    selectedState,
    currentStep,
  ]);

  const clearStep4TeleTimer = useCallback(() => {
    if (step4TeleIntervalRef.current) {
      clearInterval(step4TeleIntervalRef.current);
      step4TeleIntervalRef.current = null;
    }
  }, []);

  const completeStep4TeleOpening = useCallback(() => {
    if (!telegramLink || step4TeleOpenOnceRef.current) return;
    step4TeleOpenOnceRef.current = true;
    clearStep4TeleTimer();
    window.open(telegramLink, "_blank");
    setTeleOpenProgress(1);
    setStep4TeleGateDone(true);
  }, [telegramLink, clearStep4TeleTimer]);

  useEffect(() => {
    if (currentStep !== 4) {
      clearStep4TeleTimer();
      setStep4TeleGateDone(false);
      setTeleOpenProgress(0);
      step4TeleOpenOnceRef.current = false;
    }
  }, [currentStep, clearStep4TeleTimer]);

  const STEP4_AUTO_OPEN_MS = 5000;

  useEffect(() => {
    if (
      currentStep !== 4 ||
      !isOnboarding ||
      !isAuthenticated ||
      !telegramLink ||
      telegramConnected ||
      step4TeleGateDone
    ) {
      clearStep4TeleTimer();
      return;
    }

    setTeleOpenProgress(0);
    const started = Date.now();
    step4TeleIntervalRef.current = setInterval(() => {
      const p = Math.min(1, (Date.now() - started) / STEP4_AUTO_OPEN_MS);
      setTeleOpenProgress(p);
      if (p >= 1) {
        completeStep4TeleOpening();
      }
    }, 50);
    return () => clearStep4TeleTimer();
  }, [
    currentStep,
    isOnboarding,
    isAuthenticated,
    telegramLink,
    telegramConnected,
    step4TeleGateDone,
    clearStep4TeleTimer,
    completeStep4TeleOpening,
  ]);

  useEffect(() => {
    if (!isAuthenticated || loading) return;
    if (!isOnboarding || reminder) return;
    if (!pendingPostLoginAutoSaveRef.current) return;
    if (postLoginAutoSaveAttemptedRef.current) return;

    const hasLocations = selectedAreas.length > 0;
    const hasCategories = hasReminderCategorySelection(selectedCategories);
    const wantsFilterExpiring =
      selectedNotifySections.includes("expiring_today");
    if (wantsFilterExpiring && !hasLocations && !hasCategories) {
      pendingPostLoginAutoSaveRef.current = false;
      writeReminderOnboardingDraft({
        selectedAreas,
        selectedCategories,
        selectedNotifySections,
        daysOfWeek,
        notificationTime,
        selectedState,
        currentStep,
        pendingPostLoginSave: false,
      });
      return;
    }

    postLoginAutoSaveAttemptedRef.current = true;
    pendingPostLoginAutoSaveRef.current = false;

    writeReminderOnboardingDraft({
      selectedAreas,
      selectedCategories,
      selectedNotifySections,
      daysOfWeek,
      notificationTime,
      selectedState,
      currentStep,
      pendingPostLoginSave: false,
    });

    (async () => {
      const ok = await handleSave();
      if (!ok) {
        postLoginAutoSaveAttemptedRef.current = false;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run once per post-login draft when criteria met
  }, [
    isAuthenticated,
    loading,
    isOnboarding,
    reminder,
    selectedAreas,
    selectedCategories,
    selectedNotifySections,
    daysOfWeek,
    notificationTime,
    selectedState,
    currentStep,
  ]);

  const applyPostLoginReminderDraft = () => {
    const parsed = readReminderOnboardingDraft();
    if (!parsed) {
      pendingPostLoginAutoSaveRef.current = false;
      return;
    }
    pendingPostLoginAutoSaveRef.current = Boolean(parsed.pendingPostLoginSave);
    const areas = parsed.selectedAreas ?? [];
    setSelectedAreas(areas);
    setSelectedCategories(
      normalizeReminderCategoriesForForm(parsed.selectedCategories ?? []),
    );
    setSelectedNotifySections(
      normalizeNotifySectionsForClient(parsed.selectedNotifySections),
    );
    setDaysOfWeek(
      Array.isArray(parsed.daysOfWeek) ? [...parsed.daysOfWeek] : [],
    );
    setNotificationTime(parsed.notificationTime ?? "09:00");
    const stateName =
      parsed.selectedState && ALL_STATES.includes(parsed.selectedState)
        ? parsed.selectedState
        : inferStateFromAreas(areas);
    setSelectedState(stateName);
    const step = parsed.currentStep;
    setCurrentStep(
      typeof step === "number" ? Math.min(Math.max(step, 1), 4) : 1,
    );
  };

  const hydrateDealFilterPrefillFromBrowse = useCallback(async () => {
    if (!isAuthenticated || dealFiltersPrefilledRef.current) return;
    try {
      const res = await userAPI.getFilterPreferences();
      const fp = res.data?.filterPreferences;
      if (!fp) return;

      setSelectedAreas((prev) =>
        prev.length > 0 ? prev : reminderAreasFromDealAreaPreference(fp.area),
      );
      setSelectedCategories((prev) => {
        if (prev.length > 0) return prev;
        return normalizeReminderCategoriesForForm(
          reminderCategoriesFromDealCategoryPreference(fp.category),
        );
      });
    } catch (e) {
      logger.error("Failed to prefill reminder from deal filters:", e);
    } finally {
      dealFiltersPrefilledRef.current = true;
    }
  }, [isAuthenticated]);

  const fetchReminder = async () => {
    try {
      setLoading(true);
      const response = await api.get("/reminders");
      if (response.data && response.data.reminder) {
        clearReminderOnboardingDraft();
        pendingPostLoginAutoSaveRef.current = false;
        postLoginAutoSaveAttemptedRef.current = false;
        const reminderData = response.data.reminder;
        setReminder(reminderData);
        const savedAreas = reminderData.filters?.area || [];
        setSelectedAreas(savedAreas);
        setSelectedCategories(
          normalizeReminderCategoriesForForm(reminderData.filters?.category || []),
        );
        setSelectedNotifySections(
          normalizeNotifySectionsForClient(reminderData.notifySections),
        );
        setDaysOfWeek(
          Array.isArray(reminderData.daysOfWeek)
            ? [...reminderData.daysOfWeek]
            : [],
        );
        setNotificationTime(reminderData.notificationTime || "09:00");
        setTelegramConnected(!!reminderData.telegramChatId);
        setIsOnboarding(false);

        if (savedAreas.length > 0) {
          for (const [state, areas] of Object.entries(STATE_TO_AREAS)) {
            if (areas.includes(savedAreas[0])) {
              setSelectedState(state);
              break;
            }
          }
        }

        if (reminderData._id) {
          generateTelegramLink(reminderData._id);
        }
        dealFiltersPrefilledRef.current = true;
      } else {
        setReminder(null);
        setIsOnboarding(true);
        setTelegramConnected(false);
        trackEvent("reminder_setup_start");
        applyPostLoginReminderDraft();
        await hydrateDealFilterPrefillFromBrowse();
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        logger.error("Error fetching reminder:", error);
      } else {
        setReminder(null);
        setIsOnboarding(true);
        setTelegramConnected(false);
        trackEvent("reminder_setup_start");
        applyPostLoginReminderDraft();
        await hydrateDealFilterPrefillFromBrowse();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedAreas.length) return;
    setSelectedState((prev) => {
      if (prev) return prev;
      return inferStateFromAreas(selectedAreas) || "";
    });
  }, [selectedAreas]);

  const generateTelegramLink = (reminderId) => {
    // Encode reminderId as base64url for Telegram deep links (URL-safe)
    const encodedId = btoa(reminderId)
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/g, "");
    const botUsername =
      process.env.REACT_APP_TELEGRAM_BOT_USERNAME || "Loops_deals_bot";
    const link = `https://t.me/${botUsername}?start=${encodedId}`;
    setTelegramLink(link);
  };

  const handleAreaToggle = (area) => {
    setSelectedAreas((prev) => {
      if (prev.includes(area)) {
        return prev.filter((a) => a !== area);
      }
      // Limit to 3 locations for query efficiency
      if (prev.length >= 3) {
        setError("Maximum 3 locations allowed. Please deselect one first.");
        setTimeout(() => setError(""), 3000);
        return prev;
      }
      setError(""); // Clear error on successful selection
      setStep2RequiredError(false);
      return [...prev, area];
    });
  };

  const closeReminderAreaPicker = () => {
    setAreaPickerOpen(false);
    setDialogQuery("");
    setDialogStep("state");
    setDialogBrowsingState("");
  };

  const handleSelectAllCategories = () => {
    setSelectedCategories([]);
    setError("");
    setStep2RequiredError(false);
  };

  const handleCategoryToggle = (category) => {
    setSelectedCategories((prev) => {
      const allValues = allReminderCategoryValues();

      if (isAllReminderCategoriesSelected(prev)) {
        return [category];
      }

      const next = prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category];

      if (
        next.length === 0 ||
        (next.length === allValues.length &&
          allValues.every((v) => next.includes(v)))
      ) {
        return [];
      }

      return next;
    });
    setError("");
    setStep2RequiredError(false);
  };

  const handleNotifySectionToggle = (sectionKey) => {
    setSelectedNotifySections((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      if (current.includes(sectionKey)) {
        const next = current.filter((s) => s !== sectionKey);
        return next.length > 0 ? next : [...DEFAULT_NOTIFY_SECTIONS];
      }
      return [...current, sectionKey];
    });
  };

  const renderNotifyPreferenceSection = () => (
    <Box sx={{ mb: 5 }}>
      <Typography sx={reminderSectionTitleSx}>Notify me about</Typography>
      <Typography sx={{ ...reminderSectionSubtitleSx, mb: 1.5 }}>
        Choose what to remind you.
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {NOTIFY_SECTION_OPTIONS.map((option) => {
          const isSelected = selectedNotifySections.includes(option.key);
          return (
            <Box
              key={option.key}
              onClick={() => handleNotifySectionToggle(option.key)}
              sx={{
                cursor: "pointer",
                background: "#fff",
                color: "#000",
                borderRadius: "14px",
                px: 2,
                py: 1.25,
                transition: "all 0.2s ease",
                border: isSelected ? "1px solid #000" : "1px solid #ddd",
                "&:hover": { bgcolor: "#fff" },
              }}
            >
              <Typography sx={reminderOptionCardTitleSx}>
                {option.label}
              </Typography>
              <Typography sx={{ ...reminderOptionCardSubtitleSx, mt: 0.25 }}>
                {option.description}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  const renderTimeSection = () => (
    <Box sx={{ mb: 5, mt: 0 }}>
      <Typography sx={reminderSectionTitleSx}>Time</Typography>
      <Typography sx={{ ...reminderSectionSubtitleSx, mb: 1.5 }}>
        We'll send reminders at the time you select
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {REMINDER_NOTIFICATION_TIMES.map((time) => (
          <Box
            key={time.value}
            onClick={() => setNotificationTime(time.value)}
            sx={reminderChipSx(notificationTime === time.value)}
          >
            {time.label}
          </Box>
        ))}
      </Box>
    </Box>
  );

  const renderFrequencySection = (filterErrorMessage) => (
    <Box sx={{ mb: 5 }}>
      <Typography sx={reminderSectionTitleSx}>Frequency</Typography>
      <Typography sx={{ ...reminderSectionSubtitleSx, mb: 1.5 }}>
        We'll only send reminders on the days you select
      </Typography>
      {filterErrorMessage ? (
        <Typography sx={{ ...reminderInlineErrorSx, mb: 2 }}>
          {filterErrorMessage}
        </Typography>
      ) : null}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        <Box
          onClick={handleFrequencyDaily}
          sx={reminderChipSx(daysOfWeek.length === 0)}
        >
          Daily
        </Box>
        {REMINDER_DAY_OPTIONS.map((day) => {
          const selected = daysOfWeek.includes(day.value);
          return (
            <Box
              key={day.value}
              onClick={() => handleDayToggle(day.value)}
              sx={reminderChipSx(selected)}
            >
              {day.label}
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  const handleFrequencyDaily = () => setDaysOfWeek([]);

  const handleDayToggle = (dayValue) => {
    setDaysOfWeek((prev) => {
      if (prev.includes(dayValue)) {
        const next = prev.filter((d) => d !== dayValue);
        return next.length === 0 ? [] : next;
      }
      return [...prev, dayValue].sort((a, b) => a - b);
    });
  };

  const hasChanges = useMemo(() => {
    if (!reminder) return false;
    const savedAreas = reminder.filters?.area || [];
    const savedCategories = reminder.filters?.category || [];
    const normCat = (arr) => {
      const a = Array.isArray(arr) ? arr : [];
      if (isAllReminderCategoriesSelected(a)) {
        return [...allReminderCategoryValues()].sort().join("\0");
      }
      return [...new Set(a.map(String))].sort().join("\0");
    };
    const savedNotifySections = normalizeNotifySectionsForClient(
      reminder.notifySections,
    );
    const savedTime = reminder.notificationTime || "09:00";
    const savedDays = Array.isArray(reminder.daysOfWeek)
      ? [...reminder.daysOfWeek].sort((a, b) => a - b)
      : [];
    const currentDays =
      daysOfWeek.length === 0 ? [] : [...daysOfWeek].sort((a, b) => a - b);
    if (
      selectedAreas.length !== savedAreas.length ||
      normCat(selectedCategories) !== normCat(savedCategories) ||
      selectedNotifySections.length !== savedNotifySections.length
    )
      return true;
    if (
      selectedAreas.some((a, i) => a !== savedAreas[i]) ||
      selectedNotifySections.some((s, i) => s !== savedNotifySections[i])
    )
      return true;
    if (notificationTime !== savedTime) return true;
    if (currentDays.length !== savedDays.length) return true;
    if (currentDays.some((d, i) => d !== savedDays[i])) return true;
    return false;
  }, [
    reminder,
    selectedAreas,
    selectedCategories,
    selectedNotifySections,
    notificationTime,
    daysOfWeek,
  ]);

  const handleSave = async () => {
    const wantsFilterExpiring =
      selectedNotifySections.includes("expiring_today");
    if (
      wantsFilterExpiring &&
      selectedAreas.length === 0 &&
      !hasReminderCategorySelection(selectedCategories)
    ) {
      setError("Please select at least one area or category");
      setStep2RequiredError(true);
      return false;
    }
    setStep2RequiredError(false);

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const reminderData = {
        filters: {
          area: selectedAreas,
          category: selectedCategories,
          brand: [],
          timeOfDay: [],
        },
        frequency: "daily",
        notifySections: selectedNotifySections,
        notificationTime,
        daysOfWeek:
          daysOfWeek.length === 0 ? [] : [...daysOfWeek].sort((a, b) => a - b),
      };

      let response;
      if (reminder) {
        response = await api.put(`/reminders/${reminder._id}`, reminderData);
      } else {
        response = await api.post("/reminders", reminderData);
      }

      if (response.data && response.data.reminder) {
        clearReminderOnboardingDraft();
        setReminder(response.data.reminder);

        // Use Telegram link from backend response, or generate if not provided
        if (response.data.telegramLink) {
          setTelegramLink(response.data.telegramLink);
        } else if (
          response.data.reminder._id &&
          !response.data.reminder.telegramChatId
        ) {
          generateTelegramLink(response.data.reminder._id);
        }

        // Move to step 4 (if in onboarding)
        if (isOnboarding) {
          setCurrentStep(4);
          setSuccess("Preferences saved successfully");
        } else {
          // Show success toast only for updates (not on step 4)
          const message = "Reminder updated successfully";
          setSuccess(message);
          setShowSuccessToast(true);
        }

        return true;
      }
      return false;
    } catch (error) {
      logger.error("Error saving reminder:", error);
      logger.error("Error details:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        url: error.config?.url,
        method: error.config?.method,
      });

      // Show more detailed error message
      let errorMessage = "Failed to save reminder preferences";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 401) {
        errorMessage = "Please log in to save reminder preferences";
      } else if (error.response?.status === 404) {
        errorMessage =
          "Server endpoint not found. Please check if server is running.";
      } else if (error.response?.status === 500) {
        errorMessage = "Server error. Please try again later.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteReminder = async () => {
    if (!reminder) return;

    try {
      setSaving(true);
      setDeleteConfirmOpen(false);
      await api.delete(`/reminders/${reminder._id}`);
      setReminder(null);
      setSelectedAreas([]);
      setSelectedCategories([]);
      setSelectedNotifySections([...DEFAULT_NOTIFY_SECTIONS]);
      setDaysOfWeek([]);
      setNotificationTime("09:00");
      setTelegramConnected(false);
      setTelegramLink("");
      setSuccess("");
      setError("");
      clearReminderOnboardingDraft();
      dealFiltersPrefilledRef.current = false;
      setIsOnboarding(true);
      setCurrentStep(1);
      await hydrateDealFilterPrefillFromBrowse();
    } catch (error) {
      logger.error("Error deleting reminder:", error);
      setError(error.response?.data?.message || "Failed to delete reminder");
    } finally {
      setSaving(false);
    }
  };

  const isNewSetup = isOnboarding;
  const remindersReturnEncoded = encodeURIComponent("/reminders");

  const goToLoginForTelegramConnect = useCallback(() => {
    writeReminderOnboardingDraft({
      selectedAreas,
      selectedCategories,
      selectedNotifySections,
      daysOfWeek,
      notificationTime,
      selectedState,
      currentStep: 4,
      pendingPostLoginSave: true,
    });
    navigate(`/login?returnUrl=${remindersReturnEncoded}`);
  }, [
    selectedAreas,
    selectedCategories,
    selectedNotifySections,
    daysOfWeek,
    notificationTime,
    selectedState,
    navigate,
    remindersReturnEncoded,
  ]);

  useEffect(() => {
    if (loading || !isOnboarding || isAuthenticated || currentStep !== 4) return;
    goToLoginForTelegramConnect();
  }, [
    loading,
    isOnboarding,
    isAuthenticated,
    currentStep,
    goToLoginForTelegramConnect,
  ]);

  const continueFromStep3 = async () => {
    const wantsFilterExpiring =
      selectedNotifySections.includes("expiring_today");
    const hasLocations = selectedAreas.length > 0;
    const hasCategories = hasReminderCategorySelection(selectedCategories);
    if (wantsFilterExpiring && !hasLocations && !hasCategories) {
      setStep2RequiredError(true);
      return;
    }
    setStep2RequiredError(false);

    if (!isAuthenticated) {
      goToLoginForTelegramConnect();
      return;
    }

    await handleSave();
  };

  if (loading) {
    return (
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          bgcolor: appBackgroundColor,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 10000,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <UserMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        navLinks={navLinks}
      />
      <Dialog
        open={exampleDigestDialogOpen}
        onClose={() => setExampleDigestDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="reminder-example-dialog-title"
        scroll="paper"
        sx={{ zIndex: 10500 }}
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: "26px",
            border: `1px solid ${uiNeutrals.surfaceBorder}`,
            boxShadow: "0 12px 32px rgba(0, 0, 0, 0.05)",
            maxHeight: "min(90dvh, 720px)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            backdropFilter: "blur(5px) saturate(1.05)",
            WebkitBackdropFilter: "blur(5px) saturate(1.05)",
          },
        }}
      >
        <DialogTitle
          id="reminder-example-dialog-title"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            fontSize: "1.05rem",
            fontWeight: 600,
          }}
        >
          <span>Example message</span>
          <IconButton
            aria-label="Close"
            onClick={() => setExampleDigestDialogOpen(false)}
            edge="end"
            sx={closeIconButtonSx}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            pt: 0,
            flex: "1 1 auto",
            minHeight: 0,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <Box
            sx={{
              bgcolor: "#fff",
              border: `1px solid ${uiNeutrals.surfaceBorder}`,
              borderLeft: "3px solid #0088cc",
              borderRadius: 2,
              p: 1.75,
              boxShadow: "0 1px 4px rgba(0, 0, 0, 0.035)",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.9375rem",
                fontWeight: 700,
                color: "#111",
                mb: 1.5,
                lineHeight: 1.4,
              }}
            >
              ⏰{" "}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {REMINDER_DIGEST_EXAMPLE_DEAL_COUNT} deals
              </Box>{" "}
              ending soon. Check them out before they&apos;re gone!
            </Typography>

            <Box sx={{ mb: 1.5 }}>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  lineHeight: 1.55,
                  color: "#111",
                  mb: 0.5,
                }}
              >
                📅 {formatReminderDigestDateMalaysia()}
              </Typography>

            {selectedNotifySections.includes("expiring_today") && (
              <>
                {REMINDER_DIGEST_EXAMPLE.filterLines.map((line) => (
                  <Typography
                    key={line}
                    sx={{
                      fontSize: "0.875rem",
                      lineHeight: 1.55,
                      color: "#111",
                    }}
                  >
                    {line}
                  </Typography>
                ))}
                <Typography
                  sx={{ fontSize: "0.8125rem", mt: 1, color: "#0088cc" }}
                >
                  {REMINDER_DIGEST_EXAMPLE.footerHint}
                </Typography>
              </>
            )}
            </Box>

            {REMINDER_DIGEST_EXAMPLE.sections.map((section) => (
              <Box key={section.label} sx={{ mb: 1.5 }}>
                <Typography
                  sx={{ fontSize: "0.8125rem", fontWeight: 700, mb: 1 }}
                >
                  {section.label}
                </Typography>
                {selectedNotifySections.includes("expiring_today") && (
                  <>
                    {section.filterDeals.map((deal) => (
                      <Box
                        key={`${section.label}-${deal.num}`}
                        sx={{ mb: 1.25 }}
                      >
                        <Typography
                          sx={{ fontSize: "0.875rem", lineHeight: 1.5 }}
                        >
                          <Box component="span" sx={{ fontWeight: 700 }}>
                            {deal.num}
                          </Box>{" "}
                          <Box component="span" sx={{ fontWeight: 700 }}>
                            {deal.brand}
                          </Box>
                        </Typography>
                        <Typography
                          sx={{ fontSize: "0.875rem", lineHeight: 1.45 }}
                        >
                          {deal.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.8125rem",
                            color: "#555",
                            mt: 0.25,
                          }}
                        >
                          {deal.schedule}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.8125rem",
                            color: "#0088cc",
                            mt: 0.25,
                            wordBreak: "break-all",
                          }}
                        >
                          {deal.linkHint}
                        </Typography>
                      </Box>
                    ))}
                  </>
                )}
              </Box>
            ))}
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          if (!saving) setDeleteConfirmOpen(false);
        }}
        maxWidth="xs"
        fullWidth
        aria-labelledby="delete-reminder-dialog-title"
        sx={{ zIndex: 10501 }}
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: "25px",
            border: "1px solid rgba(0, 0, 0, 0.08)",
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            backdropFilter: "blur(5px) saturate(1.05)",
            WebkitBackdropFilter: "blur(5px) saturate(1.05)",
          },
        }}
      >
        <DialogTitle
          id="delete-reminder-dialog-title"
          sx={{ fontSize: "1.05rem", fontWeight: 600, pb: 0.5 }}
        >
          Delete reminder?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#555", fontSize: "0.9rem" }}>
            This removes your reminder preferences and Telegram link from Loops.
            You can set them up again anytime.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            disabled={saving}
            color="inherit"
            sx={{ textTransform: "none", fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteReminder}
            disabled={saving}
            variant="text"
            disableElevation
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 999,
              px: 2.5,
              py: 0.75,
              minWidth: 0,
              color: "#dc2626",
              bgcolor: "#fee2e2",
              "&:hover": {
                bgcolor: "#fecaca",
                color: "#b91c1c",
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: appBackgroundColor,
          zIndex: 10000,
          display: "flex",
          flexDirection: "column",
          transition: "none !important",
          animation: "none !important",
          transform: "none !important",
        }}
      >
        {/* Header — matches DealsTabsContainer shell padding / frosted chrome */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 999,
            flexShrink: 0,
            pt: `calc(env(safe-area-inset-top, 0px) + ${DEALS_HEADER_TOP_GAP_PX}px)`,
            pb: `${DEALS_HEADER_BOTTOM_PADDING_PX}px`,
            bgcolor: "transparent",
            overflow: "visible",
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 0,
              pointerEvents: "none",
              backdropFilter: "blur(20px) saturate(1.08)",
              WebkitBackdropFilter: "blur(20px) saturate(1.08)",
              maskImage:
                "linear-gradient(to bottom, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.5) 38%, rgba(0,0,0,0.18) 68%, rgba(0,0,0,0) 100%)",
              WebkitMaskImage:
                "linear-gradient(to bottom, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.5) 38%, rgba(0,0,0,0.18) 68%, rgba(0,0,0,0) 100%)",
            }}
          />
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              px: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              width: "100%",
              minHeight: `${DEALS_HEADER_ROW_MIN_HEIGHT_PX}px`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={glassyButtonWrapperSx}>
                <Box
                  component="img"
                  src={`${process.env.PUBLIC_URL || ""}/assets/menu.png`}
                  alt=""
                  sx={{ width: 24, height: 24, display: "block", objectFit: "contain", pointerEvents: "none" }}
                />
                <IconButton onClick={() => setMenuOpen(true)} aria-label="Menu" sx={glassyIconButtonSx} />
              </Box>

              <Typography sx={reminderPageTitleSx}>Reminder</Typography>
            </Box>

            {/* Update button in header — only when draft differs from saved (or saving) */}
            {!isOnboarding &&
              reminder &&
              (saving || hasChanges) && (
                <CustomButton
                  variant="primary"
                  onClick={handleSave}
                  disabled={saving}
                  sx={{
                    minWidth: 70,
                    paddingX: 1.5,
                    paddingY: 0.75,
                    fontSize: "14px",
                  }}
                  preventSpam={false}
                >
                  {saving ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    "Update"
                  )}
                </CustomButton>
              )}
          </Box>
        </Box>

        {/* Body: scroll + footer — capped width on large screens */}
        <Box
          sx={{
            flex: 1,
            width: "100%",
            maxWidth: REMINDER_PAGE_MAX_CONTENT_WIDTH_PX,
            mx: "auto",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Snackbar
            open={showSuccessToast}
            autoHideDuration={3000}
            onClose={() => setShowSuccessToast(false)}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              onClose={() => setShowSuccessToast(false)}
              severity="success"
              sx={{ width: "100%" }}
            >
              {success || "Reminder preferences saved"}
            </Alert>
          </Snackbar>

          {/* Content */}
          <Box
            ref={scrollContentRef}
            sx={{
              flex: 1,
              overflowY: "auto",
              overscrollBehavior: "contain",
              px: 2,
              pt: dealsScrollContentPaddingTop,
              pb: 2,
              "&::-webkit-scrollbar": {
                display: "none",
              },
              msOverflowStyle: "none",
              scrollbarWidth: "none",
            }}
          >
            {/* Error Screen */}
            {error && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "calc(100vh - 200px)",
                  textAlign: "center",
                  px: 2,
                }}
              >
                <ErrorOutlineIcon
                  sx={{ color: "#dc2626", fontSize: 64, mb: 2 }}
                />
                <Typography
                  sx={{
                    ...reminderHeroTitleSx,
                    mb: 1,
                    color: "#dc2626",
                  }}
                >
                  Error
                </Typography>
                <Typography
                  sx={{
                    ...reminderHeroSubtitleSx,
                    maxWidth: 320,
                    mb: 3,
                  }}
                >
                  {error}
                </Typography>
                <CustomButton
                  variant="primary"
                  onClick={() => setError("")}
                  sx={{ minWidth: 120 }}
                  preventSpam={false}
                >
                  Try Again
                </CustomButton>
              </Box>
            )}

            {/* Hide content when error is shown */}
            {!error && (
              <>
                {/* Step 1 - Intro (only for new setup) */}
                {isNewSetup && currentStep === 1 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: "calc(100vh - 200px)",
                      textAlign: "center",
                      px: 2,
                    }}
                  >
                    <NotificationsOutlinedIcon
                      sx={{ fontSize: 56, color: "#0088cc", mb: 2 }}
                    />
                    <Typography sx={{ ...reminderHeroTitleSx, mb: 1 }}>
                      Telegram Expiry Reminders
                    </Typography>
                    <Typography
                      sx={{
                        ...reminderHeroSubtitleSx,
                        maxWidth: 280,
                      }}
                    >
                      Get Telegram alerts for deals that are ending soon.
                    </Typography>

                    <CustomButton
                      variant="secondary"
                      onClick={() => setExampleDigestDialogOpen(true)}
                      sx={{
                        mt: 3,
                        py: 1.25,
                        bgcolor: uiNeutrals.surface,
                        borderColor: uiNeutrals.surfaceBorder,
                        color: "#333",
                        "&:hover": {
                          bgcolor: uiNeutrals.surfaceHover,
                          borderColor: uiNeutrals.surfaceBorder,
                        },
                      }}
                      preventSpam={false}
                    >
                      See example
                    </CustomButton>
                  </Box>
                )}

                {/* Filter Preferences (always visible for existing users, step 2 for new) */}
                {(!isNewSetup || currentStep === 2) && (
                  <Box sx={{ mb: 5 }}>
                    {renderNotifyPreferenceSection()}
                    <Divider sx={{ my: 4, ...lightDividerSx }} />

                    {isNewSetup &&
                      currentStep === 2 &&
                      step2RequiredError &&
                      selectedAreas.length === 0 &&
                      !hasReminderCategorySelection(selectedCategories) &&
                      selectedNotifySections.includes("expiring_today") && (
                        <Typography sx={{ ...reminderInlineErrorSx, mb: 2 }}>
                          Pick at least one location or category.
                        </Typography>
                      )}
                    {/* Areas */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography sx={{ ...reminderSectionTitleSx, mb: 0 }}>
                        Locations
                      </Typography>
                      {selectedAreas.length > 0 && (
                        <Typography sx={reminderSectionMetaSx}>
                          {selectedAreas.length}/3 selected
                        </Typography>
                      )}
                    </Box>
                    <Typography sx={{ ...reminderSectionSubtitleSx, mb: 1.5 }}>
                      Select up to 3 areas.
                    </Typography>
                    {/* Selected areas display - show all selected areas above dropdown */}
                    {selectedAreas.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          {selectedAreas.map((area) => (
                            <Box
                              key={area}
                              onClick={() => handleAreaToggle(area)}
                              sx={reminderChipSx(true)}
                            >
                              {area}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Area picker trigger */}
                    <Box
                      onClick={() => {
                        setDialogStep("state");
                        setDialogBrowsingState("");
                        setDialogQuery("");
                        setAreaPickerOpen(true);
                      }}
                      sx={{
                        cursor: "pointer",
                        width: "100%",
                        border: "1px solid #ddd",
                        borderRadius: "12px",
                        background: "white",
                        px: 2,
                        py: 1.25,
                        mb: 1,
                        "&:hover": { borderColor: "#bbb" },
                      }}
                    >
                      <Typography sx={reminderFieldLabelSx}>
                        Search area
                      </Typography>
                      <Typography sx={reminderFieldHintSx}>
                        {selectedAreas.length >= 3
                          ? "Maximum 3 locations reached"
                          : "Select a state and area"}
                      </Typography>
                    </Box>

                    <AreaBrowsePickerDialog
                      open={areaPickerOpen}
                      onClose={closeReminderAreaPicker}
                      step={dialogStep}
                      browsingState={dialogBrowsingState}
                      query={dialogQuery}
                      onQueryChange={setDialogQuery}
                      onBack={() => {
                        setDialogStep("state");
                        setDialogBrowsingState("");
                        setDialogQuery("");
                      }}
                      onSelectState={(state) => {
                        setSelectedState(state);
                        setDialogBrowsingState(state);
                        setDialogStep("area");
                        setDialogQuery("");
                      }}
                      variant="multi"
                      selectedAreasMulti={selectedAreas}
                      onToggleAreaMulti={handleAreaToggle}
                      maxSelections={3}
                    />

                    {/* Divider between Locations and Categories */}
                    <Divider sx={{ my: 4, ...lightDividerSx }} />

                    {/* Categories */}
                    <Box sx={{ mt: 4 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography sx={{ ...reminderSectionTitleSx, mb: 0 }}>
                          Categories
                        </Typography>
                        {!isAllReminderCategoriesSelected(selectedCategories) &&
                          selectedCategories.length > 0 && (
                          <Typography sx={reminderSectionMetaSx}>
                            {selectedCategories.length} selected
                          </Typography>
                        )}
                      </Box>
                      <Typography
                        sx={{ ...reminderSectionSubtitleSx, mb: 1.5 }}
                      >
                        Select categories you're interested in, or keep All
                        selected.
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Box
                          onClick={handleSelectAllCategories}
                          sx={reminderChipSx(
                            isAllReminderCategoriesSelected(selectedCategories),
                          )}
                        >
                          All
                        </Box>
                        {REMINDER_CATEGORIES.map((cat) => {
                          const allCategoriesMode =
                            isAllReminderCategoriesSelected(selectedCategories);
                          const isSelected =
                            !allCategoriesMode &&
                            selectedCategories.includes(cat.value);
                          const isDisabled = false;
                          return (
                            <Box
                              key={cat.value}
                              onClick={() =>
                                !isDisabled && handleCategoryToggle(cat.value)
                              }
                              sx={reminderChipSx(isSelected, isDisabled)}
                            >
                              {cat.label}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Step 3 for existing users: Notification + Telegram together */}
                {!isNewSetup && (
                  <Box>
                    <Divider sx={{ my: 4, ...lightDividerSx }} />
                    {renderTimeSection()}
                    <Divider sx={{ my: 4, ...lightDividerSx }} />
                    {renderFrequencySection()}
                  </Box>
                )}

                {/* Step 3 Content: Notification Time and Frequency (for new setup) */}
                {isNewSetup && currentStep === 3 && (
                  <Box>
                    {renderTimeSection()}
                    <Divider sx={{ my: 4, ...lightDividerSx }} />
                    {renderFrequencySection(
                      step2RequiredError
                        ? "Add at least one location or category in the filters above."
                        : null,
                    )}
                  </Box>
                )}

                {/* Step 4 Content: Done - Telegram Connection (for new setup) */}
                {isOnboarding && currentStep === 4 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: "calc(100vh - 200px)",
                      textAlign: "center",
                      px: 2,
                    }}
                  >
                    {telegramConnected ? (
                      <>
                        <CheckCircleOutlineIcon
                          sx={{ color: "success.main", fontSize: 56, mb: 2 }}
                        />
                        <Typography sx={{ ...reminderHeroTitleSx, mb: 0.5 }}>
                          All Set!
                        </Typography>
                        <Typography
                          sx={{
                            ...reminderHeroSubtitleSx,
                            color: "#666",
                            mb: 2,
                          }}
                        >
                          Preferences saved successfully
                        </Typography>
                        <Typography
                          sx={{
                            ...reminderHeroSubtitleSx,
                            maxWidth: 320,
                            textAlign: "center",
                          }}
                        >
                          You're all connected! You'll receive personalized deal
                          reminders every day at your selected time. Check your
                          Telegram messages to see the latest deals matching
                          your location and category preferences.
                        </Typography>

                        {(step4TeleGateDone || telegramConnected) && (
                          <CustomButton
                            variant="secondary"
                            onClick={() => {
                              window.location.reload();
                            }}
                            sx={{
                              mt: 3,
                              py: 1.5,
                              width: "100%",
                              maxWidth: 270,
                            }}
                            preventSpam={false}
                          >
                            Back
                          </CustomButton>
                        )}
                      </>
                    ) : (
                      <>
                        <CheckCircleOutlineIcon
                          sx={{ color: "success.main", fontSize: 84, mb: 2 }}
                        />
                        <Typography sx={{ ...reminderHeroTitleSx, mb: 0.5 }}>
                          You're all set!
                        </Typography>

                        {telegramLink ? (
                          <Box
                            sx={{
                              mt: 3,
                              width: "100%",
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            <Box
                              sx={{
                                width: "100%",
                                maxWidth: 270,
                                p: 1,
                                borderRadius: 3,
                                bgcolor: reminderFrostedChromeBg,
                                backdropFilter: "blur(12px)",
                                WebkitBackdropFilter: "blur(12px)",
                              }}
                            >
                              <Box
                                sx={{
                                  position: "relative",
                                  width: "100%",
                                  borderRadius: "25px",
                                  overflow: "hidden",
                                  minHeight: 48,
                                }}
                              >
                                {/* Track + left→right fill (loading-bar style) */}
                                <Box
                                  aria-hidden
                                  sx={{
                                    position: "absolute",
                                    inset: 0,
                                    bgcolor: "#005f8a",
                                  }}
                                />
                                <Box
                                  aria-hidden
                                  sx={{
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: `${Math.round(teleOpenProgress * 100)}%`,
                                    bgcolor: "#0088cc",
                                    pointerEvents: "none",
                                    zIndex: 0,
                                    transition: "width 0.06s linear",
                                  }}
                                />
                                <CustomButton
                                  variant="primary"
                                  onClick={completeStep4TeleOpening}
                                  sx={{
                                    position: "relative",
                                    zIndex: 1,
                                    width: "100%",
                                    minHeight: 48,
                                    py: 1.25,
                                    borderRadius: "25px",
                                    backgroundColor: "transparent",
                                    color: "white",
                                    boxShadow: "none",
                                    "&:hover": {
                                      backgroundColor: "rgba(0, 0, 0, 0.12)",
                                    },
                                  }}
                                  preventSpam={false}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      justifyContent: "center",
                                    }}
                                  >
                                    <TelegramIcon sx={{ fontSize: 20 }} />
                                    Connect Telegram
                                  </Box>
                                </CustomButton>
                              </Box>
                            </Box>
                          </Box>
                        ) : (
                          <CircularProgress sx={{ mt: 3 }} size={36} />
                        )}

                        {(step4TeleGateDone || telegramConnected) && (
                          <CustomButton
                            variant="secondary"
                            onClick={() => {
                              window.location.reload();
                            }}
                            sx={{
                              mt: 3,
                              py: 1.5,
                              width: "100%",
                              maxWidth: 270,
                            }}
                            preventSpam={false}
                          >
                            Back
                          </CustomButton>
                        )}
                      </>
                    )}
                  </Box>
                )}

                {/* Delete section - at bottom of content for existing users */}
                {!isOnboarding && reminder && (
                  <Box
                    sx={{
                      mt: 5,
                      mb: 5,
                    }}
                  >
                    <Divider sx={{ mb: 3, ...lightDividerSx }} />
                    <Typography
                      sx={{
                        ...reminderSectionTitleSx,
                        color: "#d32f2f",
                      }}
                    >
                      Danger Zone
                    </Typography>
                    <CustomButton
                      variant="secondary"
                      onClick={() => setDeleteConfirmOpen(true)}
                      disabled={saving}
                      sx={{
                        color: "#d32f2f",
                        borderColor: "#d32f2f",
                        "&:hover": {
                          borderColor: "#c62828",
                          bgcolor: "rgba(211, 47, 47, 0.04)",
                        },
                      }}
                      preventSpam={false}
                    >
                      Delete Reminder
                    </CustomButton>
                  </Box>
                )}
              </>
            )}
          </Box>

          {/* Footer - Fixed at bottom */}
          <Box
            sx={{
              flexShrink: 0,
              bgcolor: reminderFrostedChromeBg,
              backdropFilter: "blur(22px) saturate(1.08)",
              WebkitBackdropFilter: "blur(22px) saturate(1.08)",
              borderTop: uiNeutrals.surfaceBorder,
              px: 2,
              pt: 2,
              pb: "calc(env(safe-area-inset-bottom, 0px) + 14px)",
            }}
          >
            {isOnboarding ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {currentStep === 1 && !isAuthenticated ? (
                  <CustomButton
                    variant="primary"
                    onClick={() =>
                      setCurrentStep((prev) => Math.min(4, prev + 1))
                    }
                    sx={{ py: 1.5 }}
                    preventSpam={false}
                  >
                    Continue
                  </CustomButton>
                ) : (
                  <Box sx={{ display: "flex", gap: 1.5 }}>
                    {currentStep > 1 && currentStep !== 4 && (
                      <CustomButton
                        variant="secondary"
                        onClick={() => {
                          setCurrentStep((prev) => Math.max(1, prev - 1));
                          setStep2RequiredError(false);
                        }}
                        disabled={saving}
                        sx={{ flex: 1, py: 1.5 }}
                        preventSpam={false}
                      >
                        Back
                      </CustomButton>
                    )}
                    {currentStep !== 4 ? (
                      <CustomButton
                        variant="primary"
                        onClick={
                          currentStep === 2
                            ? () => {
                                const wantsFilterExpiring =
                                  selectedNotifySections.includes(
                                    "expiring_today",
                                  );
                                const hasLocations = selectedAreas.length > 0;
                                const hasCategories =
                                  hasReminderCategorySelection(
                                    selectedCategories,
                                  );
                                if (
                                  wantsFilterExpiring &&
                                  !hasLocations &&
                                  !hasCategories
                                ) {
                                  setStep2RequiredError(true);
                                  return;
                                }
                                setStep2RequiredError(false);
                                setCurrentStep((prev) => Math.min(4, prev + 1));
                              }
                            : currentStep < 3
                              ? () =>
                                  setCurrentStep((prev) =>
                                    Math.min(4, prev + 1),
                                  )
                              : currentStep === 3
                                ? continueFromStep3
                                : () => {}
                        }
                        disabled={saving}
                        sx={{ flex: 1, py: 1.5 }}
                        preventSpam={false}
                      >
                        {saving ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : currentStep < 3 ? (
                          "Continue"
                        ) : currentStep === 3 ? (
                          "Continue"
                        ) : (
                          "Done"
                        )}
                      </CustomButton>
                    ) : null}
                  </Box>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  width: "100%",
                }}
              >
                {reminder && !reminder.telegramChatId && (
                  <Typography
                    sx={{
                      ...reminderSectionSubtitleSx,
                      color: "#dc2626",
                      textAlign: "center",
                    }}
                  >
                    You are not connected to Telegram yet
                  </Typography>
                )}
                {reminder && telegramLink && (
                  <CustomButton
                    variant="primary"
                    onClick={() => window.open(telegramLink, "_blank")}
                    sx={{
                      flex: 1,
                      backgroundColor: "#0088cc",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "#006ba3",
                      },
                    }}
                    preventSpam={false}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <TelegramIcon sx={{ fontSize: 20 }} />
                      {reminder.telegramChatId
                        ? "Open Telegram"
                        : "Connect Telegram"}
                    </Box>
                  </CustomButton>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default TelegramReminderSetup;
